import { Link } from "react-router-dom";
import { GraduationCap, Award, BookOpen, HeartHandshake, Target, Eye } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHead } from "@/components/Reveal";
import { DOCTOR, CLINIC, IMAGES, WHY_CHOOSE } from "@/lib/site";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-[72px]">
      <Seo
        title="About Dr. Vipul Singh | Kamana Physiotherapy & Rehab Center, Lucknow"
        description="Meet Dr. Vipul Singh — MPT, BPT, DPT, University Gold Medalist and Chief Consultant at Kamana Physiotherapy & Rehab Center, Gomti Nagar, Lucknow."
      />

      {/* HERO */}
      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <p className="mono-label text-gold">About the Clinic</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.06] max-w-3xl">
            Great physiotherapy is <span className="italic text-gold">medicine,</span> practised
            with hands and patience.
          </h1>
          <p className="mt-6 text-alabaster/70 max-w-2xl leading-relaxed">
            Kamana Physiotherapy &amp; Rehab Center is Gomti Nagar's trusted destination for
            physiotherapy and rehabilitation. We specialise in pain management, post-surgery
            recovery, sports injury rehab and mobility improvement — with personalised treatment
            plans for every age.
          </p>
        </div>
      </section>

      {/* DOCTOR PROFILE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="sticky top-24">
              <div className="relative">
                <div className="absolute -inset-3 border border-gold/30 rounded-[2rem] translate-x-3 translate-y-3" aria-hidden="true" />
                <img
                  src={IMAGES.drPhoto.src}
                  alt={`${DOCTOR.name} — ${DOCTOR.title}, Kamana Physiotherapy`}
                  className="relative rounded-[1.75rem] w-full h-[480px] object-cover object-top img-spotlight"
                />
              </div>
              <div className="mt-8 bg-gold-soft border border-gold/25 rounded-2xl p-6 flex items-start gap-4">
                <Award className="text-gold-deep shrink-0 mt-1" size={22} />
                <div>
                  <p className="mono-label text-gold-deep !text-[9px]">Highest Academic Honour</p>
                  <p className="font-display text-xl font-semibold text-teal-brand mt-1">{DOCTOR.honor}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <SectionHead label="The Specialist" title={DOCTOR.name} />
          <Reveal delay={0.1}>
            <p className="mono-label text-gold-deep mt-2">{DOCTOR.title} · {DOCTOR.quals}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {DOCTOR.creds.map((c) => (
                <span key={c} className="text-xs font-semibold bg-gold-soft text-gold-deep border border-gold/25 rounded-full px-4 py-2">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-inkmuted leading-relaxed">{DOCTOR.intro}</p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10">
              <p className="mono-label text-teal-brand flex items-center gap-2">
                <GraduationCap size={15} /> Certified Specialisations
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {DOCTOR.specializations.map((s) => (
                  <span key={s} className="text-xs font-semibold bg-teal-soft text-teal-brand border border-teal-brand/15 rounded-full px-4 py-2">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-10">
              <p className="mono-label text-teal-brand flex items-center gap-2">
                <HeartHandshake size={15} /> Key Expertise
              </p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                {DOCTOR.expertise.map((e) => (
                  <li key={e} className="flex items-start gap-2.5 text-sm text-inkmuted">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0 mt-1.5" />
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-10">
              <p className="mono-label text-teal-brand flex items-center gap-2">
                <BookOpen size={15} /> Conferences &amp; Continued Learning
              </p>
              <ul className="mt-4 space-y-2">
                {DOCTOR.conferences.map((c) => (
                  <li key={c} className="text-sm text-inkmuted border-l-2 border-gold/40 pl-4">
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AWARDS TIMELINE */}
      <section className="bg-teal-soft/60 border-y border-teal-brand/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <SectionHead label="Recognition" title="A Career Built on\nCare That's Noticed" />
          <div className="grid sm:grid-cols-3 gap-5 mt-12">
            {DOCTOR.awards.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.1}>
                <div className="h-full bg-white border border-teal-brand/10 rounded-3xl p-7 card-lift">
                  <span className="font-display text-5xl font-semibold text-gold/40">0{i + 1}</span>
                  <h3 className="font-display text-xl font-semibold text-teal-brand mt-3">{a.title}</h3>
                  <p className="text-sm text-inkmuted leading-relaxed mt-3">{a.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-5">
          <Reveal>
            <div className="h-full bg-teal-brand text-alabaster rounded-3xl p-10 card-lift">
              <Target className="text-gold" size={28} />
              <h3 className="font-display text-2xl lg:text-3xl font-semibold mt-5">Our Mission</h3>
              <p className="mt-4 text-alabaster/75 leading-relaxed text-sm lg:text-base">
                To deliver compassionate, personalised physiotherapy and rehabilitation that
                restores movement, relieves pain and improves quality of life for individuals of
                all ages — through evidence-based care and advanced therapeutic techniques.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full bg-gold-soft border border-gold/25 rounded-3xl p-10 card-lift">
              <Eye className="text-gold-deep" size={28} />
              <h3 className="font-display text-2xl lg:text-3xl font-semibold mt-5 text-teal-brand">Our Vision</h3>
              <p className="mt-4 text-inkmuted leading-relaxed text-sm lg:text-base">
                To be Lucknow's leading physiotherapy and rehabilitation centre — recognised for
                excellence in pain management, injury recovery and mobility enhancement, empowering
                our community to live healthier, more active lives.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-teal-obsidian text-alabaster grain-dark relative">
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <SectionHead dark label="Why Choose Kamana" title="Six Reasons Patients\nStay With Us" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07}>
                <div className="h-full border border-alabaster/10 rounded-3xl p-7 hover:border-gold/40 transition-colors duration-300 bg-alabaster/[0.03]">
                  <span className="mono-label text-gold !text-[9px]">0{i + 1}</span>
                  <h3 className="font-display text-xl font-semibold mt-3">{w.title}</h3>
                  <p className="text-sm text-alabaster/65 leading-relaxed mt-3">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <Link
                to="/book-appointment"
                data-testid="about-book-cta"
                className="inline-block bg-gold text-teal-obsidian font-semibold rounded-full px-8 py-4 text-sm hover:bg-alabaster transition-colors duration-300"
              >
                Meet Dr. Vipul — Book an Assessment
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
